/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// "What should we call you?" — Figma file gtBsWqJJcI6ZBuBv51RU35
// node 3281:98483. Exact layout:
//
//   Frame 1000005151 (400 × 312)
//     ├── Header (370 × 168)
//     └── Frame 1000005142 (400 × 144)
//         ├── input-field "Display name" (400 × 68)
//         └── Button "Get started"       (400 × 44)   — gap 32 above

import { useState } from 'react';
import { View } from 'react-native';
import {
	AuthField,
	AuthHeader,
	AuthInput,
	AuthLayout,
	AuthPrimaryButton,
} from '../AuthLayout';
import { validateDisplayName } from '../authValidators';

export interface EnterDisplayNameScreenProps {
	readonly onSubmit: (displayName: string) => void;
	readonly externalError?: string | null;
	readonly isSubmitting?: boolean;
}

export function EnterDisplayNameScreen({
	onSubmit,
	externalError,
	isSubmitting,
}: EnterDisplayNameScreenProps): JSX.Element {
	const [displayName, setDisplayName] = useState('');
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = (): void => {
		const result = validateDisplayName(displayName);
		if (!result.ok) {
			switch (result.reason) {
				case 'empty':
					setError('Name required');
					break;
				case 'tooShort':
					setError('At least 2 characters');
					break;
				case 'tooLong':
					setError('At most 32 characters');
					break;
				case 'invalidCharacters':
					setError('Contains invalid characters');
					break;
			}
			return;
		}
		setError(null);
		onSubmit(result.value);
	};

	const fieldError = externalError ?? error;
	const canSubmit = displayName.trim().length > 0 && !isSubmitting;

	return (
		<AuthLayout accessibilityLabel="enteringDisplayName">
			<AuthHeader title="What should we call you?" />

			<View style={{ flexDirection: 'column' }}>
				<AuthField label="Display name" error={fieldError}>
					<AuthInput
						value={displayName}
						onChangeText={(next) => {
							setDisplayName(next);
							if (error !== null) setError(null);
						}}
						placeholder="Enter your name"
						autoCapitalize="words"
						textContentType="name"
						hasError={Boolean(fieldError)}
						accessibilityLabel="ping_auth_displayname_input"
					/>
				</AuthField>

				<View style={{ height: 32 }} />

				<AuthPrimaryButton
					onPress={handleSubmit}
					disabled={!canSubmit}
					accessibilityLabel="ping_auth_displayname_continue"
				>
					{isSubmitting ? 'Saving…' : 'Get started'}
				</AuthPrimaryButton>
			</View>
		</AuthLayout>
	);
}
